import { useQuery } from '@tanstack/react-query';
import {
  fetchAdminStats, fetchExchangeHealth, fetchRevenueReport,
  type ExchangeHealthEntry,
} from '../../lib/adminApi';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value, sub, title }: { label: string; value: string | number; sub?: string; title?: string }) {
  return (
    <div className="bg-slate-800 rounded-xl p-5" title={title}>
      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-white mt-1 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}


const SEVERITY_DOT: Record<'green' | 'yellow' | 'red', string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
};

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}


const EXCHANGE_DISPLAY_NAMES: Record<string, string> = {
  ps: 'PureSpectrum',
  purespectrum: 'PureSpectrum',
  rfg: 'Research For Good',
};

function displayExchangeName(name: string): string {
  return EXCHANGE_DISPLAY_NAMES[name.toLowerCase()] ?? name.toUpperCase();
}

function exchangeSyncSeverity(lastSyncedIso: string | null): 'green' | 'yellow' | 'red' {
  if (!lastSyncedIso) return 'red';
  const diffMs = Date.now() - new Date(lastSyncedIso).getTime();
  if (diffMs < 2 * 60 * 1000) return 'green';
  if (diffMs < 10 * 60 * 1000) return 'yellow';
  return 'red';
}

const S2S_STATUS_DISPLAY: Record<string, { label: string; cls: string }> = {
  s2s_firing:         { label: 'Firing ✓',          cls: 'text-emerald-400' },
  workaround_active:  { label: 'Workaround active', cls: 'text-amber-400' },
  no_data:            { label: 'No data',           cls: 'text-slate-400' },
};

function ExchangeHealthCard({ entry }: { entry: ExchangeHealthEntry }) {
  const sev = exchangeSyncSeverity(entry.last_synced_at);
  const cpiRange = entry.cpi_min != null && entry.cpi_max != null
    ? `$${entry.cpi_min.toFixed(2)} – $${entry.cpi_max.toFixed(2)}`
    : null;
  const s2sDisplay = entry.s2s_status ? S2S_STATUS_DISPLAY[entry.s2s_status] : null;

  return (
    <div
      className="bg-slate-800 rounded-xl p-5 space-y-4"
      data-testid={`exchange-health-card-${entry.exchange_name}`}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-white">{displayExchangeName(entry.exchange_name)}</h2>
        <span
          className={`w-2.5 h-2.5 rounded-full ${SEVERITY_DOT[sev]}`}
          aria-label={`sync ${sev}`}
          data-testid="exchange-health-dot"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">Live</p>
          <p className="text-xl font-bold text-white tabular-nums">{entry.live_count.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">Paused</p>
          <p className="text-xl font-bold text-white tabular-nums">{entry.paused_count.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">Closed</p>
          <p className="text-xl font-bold text-white tabular-nums">{entry.closed_count.toLocaleString()}</p>
        </div>
      </div>

      <div className="border-t border-slate-700 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-300">Last sync</span>
          <span className="text-sm font-medium text-white">{relativeTime(entry.last_synced_at)}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-300">Synced last minute</span>
          <span className="text-sm font-medium text-white tabular-nums">{entry.synced_last_minute.toLocaleString()}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-300">Completes remaining</span>
          <span className="text-sm font-medium text-white tabular-nums">{entry.total_completes_remaining.toLocaleString()}</span>
        </div>

        {cpiRange && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-300">CPI range</span>
            <span className="text-sm font-medium text-white">{cpiRange}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-300">Circuit breakers tripped</span>
          <span
            className={`text-sm font-medium tabular-nums ${entry.circuit_breakers_tripped > 0 ? 'text-amber-400' : 'text-white'}`}
          >
            {entry.circuit_breakers_tripped.toLocaleString()}
          </span>
        </div>

        {s2sDisplay && (
          <div className="flex items-center justify-between" data-testid="exchange-health-s2s-status">
            <span className="text-xs text-slate-300">S2S Postbacks</span>
            <span className={`text-sm font-medium ${s2sDisplay.cls}`}>{s2sDisplay.label}</span>
          </div>
        )}
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'stats'], queryFn: fetchAdminStats });
  // Revenue reporting is a SEPARATE seam (/api/admin/reports/revenue) — its own
  // query so a reporting hiccup never blocks the member-facing stats.
  const { data: revenue } = useQuery({ queryKey: ['admin', 'revenue'], queryFn: fetchRevenueReport });
  const { data: exchangeHealth } = useQuery({
    queryKey: ['admin', 'exchange-health'],
    queryFn: fetchExchangeHealth,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  if (isLoading) return <div className="text-slate-400">Loading stats...</div>;
  if (!data) return <div className="text-slate-400">Failed to load stats.</div>;

  // Only LIVE exchanges (PureSpectrum / RFG). The 'manual' pseudo-exchange
  // (in-house manual projects) is not a live supplier health signal — hide it.
  const liveExchangeHealth = (exchangeHealth ?? []).filter((e) => e.exchange_name !== 'manual');

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-display text-white">Dashboard</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard label="Total Members" value={data.totalMembers} />
        <StatCard label="Joined Today" value={data.membersToday} sub={`This week: ${data.membersWeek}`} />
        <StatCard label="Points Redeemed" value={data.totalRedeemed} />
        <StatCard
          label="Today's Revenue (UTC)"
          value={revenue ? fmtMoney(revenue.todayRevenue) : '—'}
          sub="Completed surveys, net of reversals"
        />
        <StatCard
          label="Contribution Margin (30d)"
          value={revenue ? fmtMoney(revenue.rangeMargin) : '—'}
          sub="Revenue − member survey payout"
          title="Contribution margin = revenue minus member survey payout. Excludes bonuses, redemptions, and consolation. NOT profit."
        />
      </div>

      {liveExchangeHealth.length > 0 && (
        <div className="space-y-3" data-testid="exchange-health-section">
          <h2 className="text-sm font-bold text-white">Exchange Health</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {liveExchangeHealth.map((entry) => (
              <ExchangeHealthCard key={entry.exchange_name} entry={entry} />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-bold text-white mb-4">Member Signups (30 days)</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.signupChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v: string) => v.slice(5)} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#fff', fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#5B4FE8" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-bold text-white mb-4">Points Issued vs Redeemed</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[
              { name: 'Issued', value: data.totalIssued },
              { name: 'Redeemed', value: data.totalRedeemed },
              { name: 'Outstanding', value: data.outstanding },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#fff', fontSize: 12 }} />
              <Bar dataKey="value" fill="#5B4FE8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {revenue && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="revenue-section">
          <div className="bg-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-bold text-white">Revenue &amp; Contribution Margin (30 days)</h2>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="flex items-center gap-1 text-slate-400"><span className="w-2 h-2 rounded-full inline-block" style={{ background: '#5B4FE8' }} />Revenue</span>
                <span className="flex items-center gap-1 text-slate-400"><span className="w-2 h-2 rounded-full inline-block" style={{ background: '#10B981' }} />Margin</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mb-4">
              By survey START date (UTC), net of reversals as of {new Date(revenue.generatedAt).toLocaleString()}.
              {revenue.estimatedRowCount > 0 && ` ${revenue.estimatedRowCount.toLocaleString()} of ${(revenue.estimatedRowCount + revenue.actualRowCount).toLocaleString()} sessions use estimated payout.`}
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={revenue.revenueChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v: string) => v.slice(5)} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v: number) => `$${v}`} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#fff', fontSize: 12 }} formatter={(v) => fmtMoney(Number(v))} />
                <Line type="monotone" dataKey="revenue" stroke="#5B4FE8" strokeWidth={2} dot={false} name="Revenue" />
                <Line type="monotone" dataKey="margin" stroke="#10B981" strokeWidth={2} dot={false} name="Margin" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-slate-800 rounded-xl p-5" data-testid="revenue-by-source">
            <h2 className="text-sm font-bold text-white mb-4">Revenue by Source (30 days)</h2>
            {revenue.bySource.length === 0 ? (
              <p className="text-xs text-slate-500">No completed sessions in the last 30 days.</p>
            ) : (
              <div className="space-y-2">
                {revenue.bySource.map((s) => (
                  <div key={s.source} className="flex items-center justify-between border-b border-slate-700 pb-2 last:border-0">
                    <span className="text-sm text-slate-300">{displayExchangeName(s.source)}</span>
                    <span className="text-sm text-white tabular-nums">
                      {fmtMoney(s.revenue)}
                      <span className="text-slate-500"> · {fmtMoney(s.margin)} margin</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
